import { ReactNode } from 'react';

import { Box, CircularProgress, SxProps, Theme } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useLanguage } from '../../hooks';
import { openIntercom } from '../../utils/intercom';
import { paths } from '../../utils/navigation';
import { TaskAssignsLanding } from './Pricing/TaskAssignsLanding';
import { useTaskAssignerContext } from './TaskAssignerContextProvider';

const customStyles = {
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
  },
} satisfies Record<string, SxProps<Theme>>;

// Gates any Tareas asignadas entry point on plan access -- not just the list
// view. Wrap this around a route's content (inside a TaskAssignerProvider) so
// a plan-blocked account sees the landing instead of the underlying screen,
// whether that's the list, the create/edit form, or a group edit. Without
// this, a direct URL to create/edit rendered its form regardless of access:
// CreateOrEditTaskAssigned reads its own local TaskAssignerCreate context,
// not this one, so it never saw planAccessDenied (PR review residual risk).
//
// planAccessChecked guards against a flash of the real content while the
// first request is in flight (see TaskAssignerContextProvider).
export function PlanAccessGate({ children }: { children: ReactNode }) {
  const { planAccessDenied, planAccessChecked } = useTaskAssignerContext();
  const navigate = useNavigate();
  const lang = useLanguage();

  if (!planAccessChecked) {
    return (
      <Box
        sx={customStyles.loadingContainer}
        data-testid='task-assigner-loading'
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (planAccessDenied) {
    return (
      <Box data-testid='task-assigner-plan-denied'>
        <TaskAssignsLanding
          onKnowPlans={() => navigate(paths.billing.index)}
          onContactSales={() =>
            openIntercom(lang.task_assigner.pricing.landing.contactSalesIntercomMessage)
          }
        />
      </Box>
    );
  }

  return <>{children}</>;
}
